/**
 * Generic Application Ontology Program — Task 10.
 *
 * The ONE shape the Library/RAG generator accepts in place of the shared
 * WonBanking authority types it used to name in its own source.
 *
 * Every member is an OPAQUE content ref resolved from ADMITTED compiler
 * output. Nothing here is derived from a profile name, a display title, an
 * aggregation-scope string, or an application-core label: two independently
 * admitted applications differ only in the bytes of these refs, never in which
 * branch of generator code runs.
 *
 * `ContentRef` is declared locally rather than imported so this contract adds
 * no dependency to `@cogo/agentic-generator`; it is structurally the same
 * `{ ref, contentHash }` pair the publisher and the Studio bootstrap use.
 */

export interface ContentRef {
  ref: string
  contentHash: string
}

export interface ApplicationProfileLibraryContract {
  schemaVersion: 'cogo.application-profile-library-contract.v1'
  profileRef: ContentRef
  applicationOntologyPackageRef: ContentRef
  compiledOntologyRef: ContentRef
  requiredCapabilitySliceRef: ContentRef
  requiredApplicationTopologySliceRef: ContentRef
  requiredCapabilityCatalogRef: ContentRef
  runtimeConsumerCatalogRef: ContentRef
}

export const APPLICATION_PROFILE_LIBRARY_CONTRACT_SCHEMA_VERSION =
  'cogo.application-profile-library-contract.v1' as const

/**
 * The complete, closed ref member list.
 *
 * A seventh ref added to the interface without being added here fails to
 * type-check on `_ContractRefKeysAreComplete`, so the parser can never start
 * silently dropping an admitted ref.
 */
export const APPLICATION_PROFILE_LIBRARY_CONTRACT_REF_KEYS = [
  'profileRef',
  'applicationOntologyPackageRef',
  'compiledOntologyRef',
  'requiredCapabilitySliceRef',
  'requiredApplicationTopologySliceRef',
  'requiredCapabilityCatalogRef',
  'runtimeConsumerCatalogRef',
] as const satisfies readonly (keyof Omit<
  ApplicationProfileLibraryContract,
  'schemaVersion'
>)[]

type _ContractRefKeysAreComplete = Exclude<
  keyof Omit<ApplicationProfileLibraryContract, 'schemaVersion'>,
  (typeof APPLICATION_PROFILE_LIBRARY_CONTRACT_REF_KEYS)[number]
> extends never
  ? true
  : never
const _contractRefKeysAreComplete: _ContractRefKeysAreComplete = true
void _contractRefKeysAreComplete

const CONTENT_HASH = /^sha256:[a-f0-9]{64}$/

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  )
}

function isContentRef(value: unknown): value is ContentRef {
  if (!isPlainRecord(value)) return false
  const keys = Object.keys(value)
  return (
    keys.length === 2 &&
    keys.includes('ref') &&
    keys.includes('contentHash') &&
    typeof value.ref === 'string' &&
    value.ref.trim().length > 0 &&
    typeof value.contentHash === 'string' &&
    CONTENT_HASH.test(value.contentHash)
  )
}

/**
 * Closed-shape guard.
 *
 * Returns `null` — never a partially-honoured object — for anything that is
 * not exactly the schema version plus the seven admitted refs. An eighth key
 * is refused by the same length rule that refuses a missing one, so a new
 * authority-bearing field cannot slip past a denylist.
 */
export function parseApplicationProfileLibraryContract(
  value: unknown,
): ApplicationProfileLibraryContract | null {
  if (!isPlainRecord(value)) return null
  const keys = Object.keys(value)
  if (keys.length !== APPLICATION_PROFILE_LIBRARY_CONTRACT_REF_KEYS.length + 1) {
    return null
  }
  if (
    value.schemaVersion !== APPLICATION_PROFILE_LIBRARY_CONTRACT_SCHEMA_VERSION
  ) {
    return null
  }
  const refs: Record<string, ContentRef> = {}
  for (const key of APPLICATION_PROFILE_LIBRARY_CONTRACT_REF_KEYS) {
    const candidate = value[key]
    if (!isContentRef(candidate)) return null
    refs[key] = { ref: candidate.ref, contentHash: candidate.contentHash }
  }
  return {
    schemaVersion: APPLICATION_PROFILE_LIBRARY_CONTRACT_SCHEMA_VERSION,
    profileRef: refs.profileRef,
    applicationOntologyPackageRef: refs.applicationOntologyPackageRef,
    compiledOntologyRef: refs.compiledOntologyRef,
    requiredCapabilitySliceRef: refs.requiredCapabilitySliceRef,
    requiredApplicationTopologySliceRef: refs.requiredApplicationTopologySliceRef,
    requiredCapabilityCatalogRef: refs.requiredCapabilityCatalogRef,
    runtimeConsumerCatalogRef: refs.runtimeConsumerCatalogRef,
  }
}

/**
 * The one function the library bundle and the RAG orchestration both take an
 * opaque contract through.
 *
 * It resolves the admitted refs a generator step needs and NOTHING else: it
 * never reads a profile name, never re-adapts the raw runtime projection (the
 * compiler-materialized slice ref is consumed as given), and never synthesizes
 * a ref or a scope. A contract that does not parse is a named compiler
 * blocker, not a fallback.
 */
export type ApplicationProfileLibraryContractResolution =
  | { ok: true; contract: ApplicationProfileLibraryContract }
  | { ok: false; blocker: 'application-profile-library-contract-unadmitted' }

export function resolveApplicationProfileLibraryContract(
  value: unknown,
): ApplicationProfileLibraryContractResolution {
  const contract = parseApplicationProfileLibraryContract(value)
  return contract === null
    ? { ok: false, blocker: 'application-profile-library-contract-unadmitted' }
    : { ok: true, contract }
}
